import { Button, Center, Modal, ModalProps, Select, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { isNotEmpty, useForm, UseFormReturnType } from '@mantine/form';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AddDatasetParams, addDatasets, DatasetsInfo, updateDatasets } from '@mygpt/app';

import { DEFAULT_MODEL_NAME, renderSelectOption } from '@/components/Create_V2/ModelSelect';
import { useGPTStore } from '@/store';

type DatasetTextEmbeddingModelProps = {
  isEdit: boolean;
  form: UseFormReturnType<AddDatasetParams>;
  embeddingModelName: string;
};

const DatasetTextEmbeddingModel = ({ embeddingModelName, form, isEdit }: DatasetTextEmbeddingModelProps) => {
  // 是否是本地部署模式，是的话，使用本地Text Embedding
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const { t } = useTranslation();

  const MODEL_SELECT_DATA = useMemo(() => {
    return [
      {
        label: `text-embedding-3-small`,
        value: 'text-embedding-3-small',
        desc: t('dataset.model_desc.small'),
      },
      {
        label: `text-embedding-3-large`,
        value: 'text-embedding-3-large',
        desc: t('dataset.model_desc.large'),
      },
      {
        label: `BAAI/bge-m3-multi`,
        value: 'BAAI/bge-m3-multi',
        desc: t('dataset.model_desc.bge_m3'),
      },
      {
        label: `text-embedding-ada-002`,
        value: 'text-embedding-ada-002',
        desc: t('dataset.model_desc.ada'),
      },
    ];
  }, [t]);

  if (isLocalMode) {
    return null;
  }

  if (isEdit) {
    return <Text size="sm" fw={500}>{`${t('dataset.model')}${embeddingModelName}`}</Text>;
  }

  return (
    <Select
      label={
        <>
          <h3 className="gray.8">{t('dataset.model_select.title')}</h3>
          <Text c="gray.5" size="xs" className="my-1">
            {t('dataset.model_select.desc')}
          </Text>
        </>
      }
      data={MODEL_SELECT_DATA}
      allowDeselect={false}
      renderOption={renderSelectOption}
      {...form.getInputProps('embedding_model_name')}
    ></Select>
  );
};

interface Props extends ModalProps {
  info?: DatasetsInfo;
  afterSubmit?: () => void;
}

export default ({ afterSubmit, info: datasetInfo, onClose, opened }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { assetScope } = useGPTStore();
  const isCloudMode = import.meta.env.VITE_MODE !== 'local';
  const [loading, setLoading] = useState(false);

  const isEdit = useMemo(() => !!datasetInfo, [datasetInfo]);

  const form = useForm<AddDatasetParams>({
    initialValues: {
      name: '',
      description: '',
      embedding_model_name: 'text-embedding-3-large',
    },
    validate: {
      name: isNotEmpty(t('fields.dataset_name_required')),
    },
    transformValues: (values) => ({
      name: values.name?.trim(),
      description: values.description?.trim(),
      embedding_model_name: values.embedding_model_name,
    }),
  });

  const submit = async (
    values: { name: string; description: string; embedding_model_name: string },
    event?: FormEvent<HTMLFormElement>
  ) => {
    event?.preventDefault();
    setLoading(true);
    const { description, embedding_model_name, name } = values;

    // 新增或修改
    const addParams = {
      name: name,
      description: description,
      embedding_model_name: embedding_model_name,
      // Cloud版传入公司ID：有组织ID则传入，否则不传（表示个人资产）
      ...(isCloudMode && assetScope && { company_id: assetScope }),
    };
    const updateParams = { ...addParams, id: datasetInfo?.id || '' };
    const promise = datasetInfo ? updateDatasets(updateParams) : addDatasets(addParams);

    promise
      .then((res) => {
        onClose();
        form.reset();

        if (isEdit) {
          afterSubmit?.();
        } else {
          navigate(`/datasets/${res.data.id}/data-source`);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!datasetInfo) return;
    form.setValues({
      name: datasetInfo.name,
      description: datasetInfo.description,
    });
  }, [datasetInfo]);

  useEffect(() => {
    opened && form.clearErrors();
  }, [opened]);

  console.log('datasetInfo?.metadata', datasetInfo?.metadata);

  return (
    <Modal opened={opened} onClose={onClose} title="" size={'xl'} centered>
      <Stack>
        <Title order={3} ta="center">
          {isEdit ? t('dataset.edit') : t('dataset.create')}
        </Title>
        <Text ta="center" c="gray.5">
          {isEdit ? '' : t('dataset.create_desc')}
        </Text>

        <Center mt={30} mb={20}>
          <form style={{ width: '70%' }} onSubmit={form.onSubmit(submit)}>
            <Stack gap={'lg'}>
              <TextInput
                label={t('dataset.dataset_name')}
                {...form.getInputProps('name')}
                data-autofocus
                autoComplete="off"
                withAsterisk
              />
              <Textarea
                label={t('dataset.dataset_desc')}
                description={t('dataset.dataset_desc_hint')}
                minRows={3}
                {...form.getInputProps('description')}
                autoComplete="off"
              />
              <DatasetTextEmbeddingModel
                isEdit={isEdit}
                form={form}
                embeddingModelName={datasetInfo?.metadata.embedding_model_name || DEFAULT_MODEL_NAME}
              />
              <Center mt={30}>
                <Button loading={loading} type="submit">
                  {isEdit ? t('save') : t('submit')}
                </Button>
              </Center>
            </Stack>
          </form>
        </Center>
      </Stack>
    </Modal>
  );
};
